/** @format */

import { differenceInSeconds, getTime, isSameDay } from 'date-fns';
import isFnsDate from 'date-fns/isDate';
import { Timestamp } from 'firebase/firestore';
import { memoize, replace } from 'lodash';
import eachWeekendOfInterval from 'date-fns/eachWeekendOfInterval';
import isWeekend from 'date-fns/isWeekend';
import { isEmpty } from 'lodash';
import { isNumber } from 'lodash';
import { isNaN } from 'lodash';

const isValidTimestamp = (value) => {
  // Giới hạn timestamp: từ 1970 đến một thời điểm xa trong tương lai
  const minTimestamp = new Date('2024-01-01').getTime();
  const maxTimestamp = new Date('3000-01-01').getTime();

  // Kiểm tra xem giá trị có phải là số, nằm trong khoảng thời gian hợp lệ, và tạo ra một ngày hợp lệ
  return isNumber(value) && value >= minTimestamp && value <= maxTimestamp && !isNaN(new Date(value).getTime());
};

/**
 * isDateTimeString
 */
export const isDateTimeString = memoize((value) =>
  /((\d{4}(-|\/)\d{2}(-|\/)\d{2})|(\d{2}(-|\/)\d{2}(-|\/)\d{4}))(\s)(\d{2}:\d{2}:\d{2})$/.test(value)
);

/**
 * isDateString
 */
export const isDateString = memoize((value) => /(\d{4}(-|\/)\d{2}(-|\/)\d{2})|(\d{2}(-|\/)\d{2}(-|\/)\d{4})$/.test(value));

/**
 * isIsoDateString
 */
export const isIsoDateString = memoize((value) =>
  // ?(.\d{3}Z)
  /\d{4}(-|\/)\d{2}-\d{2}T\d{2}:\d{2}:\d{2}((\+\d{2}:\d{2})|(.\d{3}Z))?/.test(value)
);
/**
 * isDate
 * @param value
 * @returns
 */
export const isDate = (value) => {
  const checkIsDateString = isDateString(value);
  const checkIsDateTimeString = isDateTimeString(value);
  const checkIsIsoDateString = isIsoDateString(value);
  const checkIsFnsDate = isFnsDate(value) && !isNaN(value.getTime());
  const checkIsValidTimestamp = isValidTimestamp(value);

  return checkIsDateString || checkIsDateTimeString || checkIsIsoDateString || checkIsFnsDate || checkIsValidTimestamp;
};

/**
 * isHasWeekend
 * @param value
 * @returns
 */
export const isHasWeekend = (start, end) => {
  const isStartDate = isDate(start) || isValidTimestamp(start);
  const isEndDate = isDate(end) || isValidTimestamp(end);
  if (isStartDate && isEndDate) {
    try {
      const result = eachWeekendOfInterval({
        start: new Date(start),
        end: new Date(end)
      });

      return !isEmpty(result);
    } catch (e) {
      return false;
    }
  }

  if (isStartDate) {
    return isWeekend(new Date(start));
  }

  if (isEndDate) {
    return isWeekend(new Date(end));
  }
};

/**
 * isTimestamp
 * @param value
 * @returns
 */
export const isTimestamp = (value) => {
  return value instanceof Timestamp;
};

/**
 * checkExpired
 * @param {*} time
 * @param {*} duration
 * @returns boolean
 */

export const checkExpired = (time, duration = 10) => {
  const current = getTime(new Date());
  const second = differenceInSeconds(current, time);
  // console.log("checkExpired time current", time, current);
  // console.log("checkExpired", duration, second);
  return second > duration ? true : false;
};

/**
 * unformatValue
 * @param {*} money
 * @param {*} prefix
 * @returns
 */
export const unformatValue = (money, prefix = ',') => {
  return replace(money, new RegExp(prefix, 'g'), '');
};

/**
 * isEmptyValue
 * @param str
 * @returns
 */
export const isEmptyValue = (value) =>
  value === '' || value === undefined || value === null || (typeof value !== 'string' && typeof value !== 'number');

export const inRangeDayScheduled = (day, startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return day >= start && day <= end;
};
export const inRangeDaysScheduled = (day, scheduledDays) => {
  return scheduledDays.some(({ startDate, endDate }) => {
    return inRangeDayScheduled(day, startDate, endDate);
  });
};

// Function to check if a date is in the blocked range
export const isDateBlocked = (
  date,
  scheduledDays,
  whiteList = {
    currentRangeDates: {},
    canStartInDate: []
  }
) => {
  /**
   * Thuộc ngày đã có xe nhưng là ngày về nên có thể tao
   */
  // Kiểm tra nếu date có trong danh sách canStartInDate
  if (whiteList?.canStartInDate?.some((allowedDate) => isSameDay(date, new Date(allowedDate)))) {
    return false; // Không bị blocked nếu nằm trong canStartInDate
  }

  /**
   * Trường hợp edit thì không check ngày hiện đang set
   */
  // Chuyển đổi currentRangeDates thành đối tượng Date (nếu tồn tại)
  const currentStart = whiteList?.currentRangeDates?.start ? new Date(whiteList.currentRangeDates.start) : null;
  const currentEnd = whiteList?.currentRangeDates?.end ? new Date(whiteList.currentRangeDates.end) : null;

  // Nếu date nằm trong khoảng currentRangeDates, không bị blocked
  if (currentStart && currentEnd && date >= currentStart && date <= currentEnd) {
    return false;
  }

  /**
   * Nếu nằm trong bloked thì ko cho chọn
   */
  // Kiểm tra nếu date nằm trong scheduledDays
  return scheduledDays.some(({ startDate, endDate }) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return date >= start && date <= end;
  });
};
export const checkAcceptedEndDates = (highlightedDays) => {
  return highlightedDays.filter(({ endDate }) => {
    const end = new Date(endDate);

    // Kiểm tra nếu ngày end không thuộc bất kỳ khoảng nào
    const isValid = highlightedDays.every(({ startDate, endDate: rangeEnd }) => {
      const start = new Date(startDate);
      const rangeEndDate = new Date(rangeEnd);

      // Nếu là cùng ngày (startDate === endDate), cho phép giữ lại
      if (start.getTime() === rangeEndDate.getTime() && end.getTime() === start.getTime()) {
        return true; // Thuộc một ngày duy nhất
      }

      // Nếu thuộc bất kỳ khoảng nào, loại bỏ
      if (end >= start && end <= rangeEndDate) {
        return false; // Thuộc khoảng start -> end
      }

      return true; // Không thuộc khoảng nào
    });

    return isValid;
  });
};
export const isArraysEqual = (array1, array2) => {
  if (array1.length !== array2.length) return false; // Nhanh chóng loại trừ
  const set1 = new Set(array1);
  const set2 = new Set(array2);
  return [...set1].every((item) => set2.has(item));
};
